<template>
    <basic-card>
        <template slot="header">
            Reconciliation
        </template>
        <template v-if="matches.length > 0">
            <collapsible-content start-collapsed>
                <template slot="header">
                    Reconciled items
                </template>
                <data-table
                        :data="matches"
                        :configuration="matchesTableConfig"
                        component-id="reconciliation-matches-list"/>
            </collapsible-content>
        </template>

        <collapsible-content>
            <template slot="header">
                Account balance items for reconciliation
            </template>
            <filter-form
                    :filter-definition="queryDefinition"
                    v-model="searchQuery"
            />


            <simple-button @press="refreshCandidates">Refresh candidates</simple-button>
            <data-table
                    :data="accountBalanceItems"
                    :configuration="candidatesTableConfig"
                    :pagination="pagination"
                    @pageChanged="handlePageChanged"
                    @reconcile="reconcile"
                    component-id="reconciliation-candidates-list"
            />
        </collapsible-content>
    </basic-card>
</template>

<script>
    import BasicCard from "../components/layout/BasicCard";
    import FilterForm from "../components/filters/FilterForm";
    import SimpleButton from "../components/button/SimpleButton";
    import DataTable from "../components/table/DataTable";
    import {mapGetters} from "vuex";
    import QueryUtils from "../utils/QueryUtils";
    import Paginator from "../components/Paginator";
    import CollapsibleContent from "../components/CollapsibleContent";

    export default {
        name: "ReconciliationAccountBalanceItemsCard",
        components: {CollapsibleContent, Paginator, DataTable, SimpleButton, FilterForm, BasicCard},
        props: {
            cashEvent: Object
        },
        data: function () {
            return {
                matches: [],
                searchQuery: {parameters: {}},
                refreshDate: new Date().getTime(),
                matchesTableConfig: {
                    columns: [
                        {
                            caption: "Amount",
                            datum: d => this.$formats.formatAmount(d.exactMatchList?.[0].amount / 100)
                        },
                        {
                            caption: "Currency",
                            datum: d => d.exactMatchList?.[0].currency
                        },
                        {
                            caption: "Variable symbol",
                            datum: d => d.exactMatchList?.[0].variableSymbol
                        },
                        {
                            caption: "Message",
                            datum: d => d.exactMatchList?.[0].message
                        },
                        {
                            caption: "Type",
                            datum: d => d.type
                        }
                    ]
                },
                candidatesTableConfig: {
                    columns: [
                        {
                            caption: "",
                            datum: d => this.matches.find(match => match.exactMatchList?.[0].id == d.id) != undefined ? "R" : "",
                            style: d => this.matches.find(match => match.exactMatchList?.[0].id == d.id) != undefined ? ({'background-color': 'green'}) : undefined
                        },
                        {
                            caption: "Counter account name",
                            datum: d => d.counterAccountName
                        },
                        {
                            caption: "Amount",
                            datum: d => this.$formats.formatAmount(d.amount / 100)
                        },
                        {
                            caption: "Currency",
                            datum: d => d.currency
                        },
                        {
                            caption: "Variable symbol",
                            datum: d => d.variableSymbol
                        },
                        {
                            caption: "Specific symbol",
                            datum: d => d.specificSymbol
                        },
                        {
                            caption: "Message",
                            datum: d => d.message
                        },
                        {
                            caption: "Date",
                            datum: d => this.$formats.formatDate(d.balanceDate, false)
                        }
                    ],
                    actions: [
                        {
                            icon: 'equals',
                            name: 'reconcile',
                            accessLambda: d => this.matches.find(match => match.exactMatchList?.[0].id == d.id) == undefined,
                            tooltip: "Reconcile balance item",
                            isAsync: true
                        }
                    ]
                },
            }
        },
        computed: {
            ...mapGetters({
                "account": "banking/account",
                "currencies": "currencies/currenciesOptions",
                "accounts": "banking/accounts",
                "accountBalanceItems": "accountBalanceItems/accountBalanceItems"
            }),
            pagination() {
                return this.$store.getters['accountBalanceItems/accountBalanceItemPagination'];
            },
            queryDefinition() {
                return QueryUtils.getByType("accountBalanceItem", {
                    currencies: this.currencies,
                    accounts: this.accounts
                })
            }
        },
        methods: {
            reconcile(accountBalanceItem) {
                let match = {
                    cashEventDto: {
                        id: this.cashEvent.id,
                        operationId: this.cashEvent.operation,
                        amount: this.cashEvent.amount,
                        currency: this.cashEvent.currency,
                        accountId: this.cashEvent.account,
                        transactionReference: this.cashEvent.transactionReference,
                        date: this.cashEvent.timestamp //TODO: replace all parameters by just account balance Id and cash event Id
                    },
                    exactMatchList: [{
                        id: accountBalanceItem.id,
                        amount: accountBalanceItem.amount,
                        currency: accountBalanceItem.currency,
                        message: accountBalanceItem.message,
                        variableSymbol: accountBalanceItem.variableSymbol,
                        accountBalanceDate: accountBalanceItem.balanceDate
                    }],
                    type: "MANUAL",
                    confidence: 1
                };
                return this.$store.dispatch("reconciliationMatchs/saveReconciliationMatch", match)
                    .then(() => {
                        this.$notify({
                            group: 'application',
                            title: 'Items reconciled',
                            text: `Reconciliation of cash-event ${this.cashEvent.id} and ${accountBalanceItem.id} was saved`,
                            type: "ok"
                        });
                    })
                .then(() => this.refreshMatches())

            },
            refreshCandidates(page) {
                return this.$store.dispatch(`accountBalanceItems/refreshAccountBalanceItemsPaged`, {
                    page: page,
                    query: this.searchQuery
                });
            },
            handlePageChanged(page) {
                this.refreshCandidates(page);
            },
            cashEventChanged(cashEvent) {
                if (cashEvent == null || cashEvent == undefined) {
                    this.$set(this, 'searchQuery', {});
                    return;
                }
                let adjustedAmount = cashEvent.amount / 10000;

                this.refreshDate = new Date().getTime();
                this.$set(this, 'searchQuery', {
                    parameters:
                        {
                            account: {
                                type: "entity",
                                operator: "in",
                                value: cashEvent.account,
                                negation: false
                            },
                            amount: {
                                type: "float",
                                operator: "range",
                                value: `${adjustedAmount - Math.abs(adjustedAmount * 0.005)};${adjustedAmount + Math.abs(adjustedAmount) * 0.005}`,
                                negation: false
                            },
                            currency: {
                                type: "entity",
                                operator: "in",
                                value: cashEvent.currency,
                                negation: false
                            },
                            variableSymbol: {
                                type: "string",
                                operator: "eq",
                                value: cashEvent.transactionReference,
                                negation: false
                            }

                        }
                });

                this.refreshCandidates(0);
                this.refreshMatches();
            },
            refreshMatches() {
                return this.$store.dispatch("reconciliationMatchs/fetchByCashEventId", this.cashEvent.id)
                    .then(matches => this.matches = matches);
            }
        },
        watch: {
            cashEvent: {
                handler(newValue) {
                    this.cashEventChanged(newValue);
                },
                immediate: true
            }
        }
    }
</script>

<style scoped>

</style>